import React, { useState } from 'react';
import { IoClose, IoCheckmarkCircle, IoAlertCircle, IoChevronDown, IoChevronForward, IoDocument, IoGitCommit } from "react-icons/io5";
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import api from '../../services/api';

// ── Module-level IDE theme constants ────────────────────────────────────────
const IDE_BG = '#ffffff';
const IDE_SIDEBAR = '#f3f3f3';
const IDE_SIDEBAR_HOVER = '#e8e8e8';
const IDE_BORDER = '#e0e0e0';
const IDE_ACTIVE = '#dce9f8';
const IDE_TITLE = '#f5f5f5';
const IDE_TEXT = '#1f1f1f';
const IDE_TEXT_DIM = '#6e6e6e';
const IDE_ACCENT = '#0078d4';
const IDE_GREEN = '#008000';
const IDE_YELLOW = '#795e26';

// ── Pure helpers for Angular preview rendering ───────────────────────────────

/**
 * Converts a full frontend path (e.g. 'generated/frontend/angular/ebal008')
 * to the relative path getFrontendFile expects ('ebal008').
 */
function getRelativeFrontendPath(fullPath = '') {
    return fullPath.replace(/^generated\/frontend\/angular\//, '');
}

/**
 * Strips Angular-specific template syntax while preserving the visual DOM scaffold.
 */
function preprocessAngularTemplate(html = '') {
    return html
        .replace(/\*ngIf="[^"]*"/g, '')
        .replace(/\*ngFor="[^"]*"/g, '')
        .replace(/\*ngSwitch(?:Case|Default)?="[^"]*"/g, '')
        .replace(/\[[^\]]+\]="[^"]*"/g, '')
        .replace(/\([^)]+\)="[^"]*"/g, '')
        .replace(/\{\{[^}]*\}\}/g, '<span class="ng-preview-slot">[…]</span>')
        .replace(/#\w+/g, '')
        .replace(/ {2,}/g, ' ')
        .trim();
}

/**
 * Builds a full standalone HTML document from Angular component files for iframe preview.
 * Injects Angular Material CDN styles so mat-* elements render visually.
 */
function buildPreviewSrcDoc(fileMap, formName = '') {
    const htmlPath = Object.keys(fileMap).find(p => p.endsWith('.component.html'));
    const cssPath = Object.keys(fileMap).find(p =>
        p.endsWith('.component.css') || p.endsWith('.component.scss')
    );

    const rawHtml = (htmlPath && fileMap[htmlPath])
        ? fileMap[htmlPath]
        : '<p style="color:#999;padding:24px;font-family:sans-serif">HTML template could not be loaded</p>';
    const rawCss = cssPath ? fileMap[cssPath] : '';
    const processedHtml = preprocessAngularTemplate(rawHtml);

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${formName} — Preview</title>
  <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" rel="stylesheet" onerror="this.remove()">
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" onerror="this.remove()">
  <link href="https://unpkg.com/@angular/material@17/prebuilt-themes/indigo-pink.css" rel="stylesheet" onerror="this.remove()">
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body { margin: 0; padding: 16px; font-family: Roboto, "Helvetica Neue", sans-serif; font-size: 14px; background: #fff; color: #212121; }
    .ng-preview-slot { display: inline-block; background: rgba(0,120,212,0.1); color: #0078d4; border-radius: 3px; padding: 0 4px; font-size: 11px; font-style: italic; }
    mat-form-field, mat-label, mat-error, mat-select, mat-option, mat-card, mat-toolbar, mat-sidenav { display: block; }
    mat-icon { display: inline-block; font-family: "Material Icons"; }
    ${rawCss}
  </style>
</head>
<body>
  ${processedHtml}
</body>
</html>`;
}

// ── CodePanelContent sub-component ───────────────────────────────────────────

/**
 * Displays the source of the active tab (HTML / CSS / TS).
 * In enhanced mode with showDiff=true, uses ReactDiffViewer for changed files.
 */
const CodePanelContent = ({ tab, previewFiles, proposedChanges, showDiff }) => {
    const extsByTab = {
        html: ['.component.html'],
        css: ['.component.css', '.component.scss'],
        ts: ['.component.ts'],
    };

    const findContent = (files, tabKey) => {
        const exts = extsByTab[tabKey] || [];
        const key = Object.keys(files).find(p => exts.some(ext => p.endsWith(ext)));
        return key ? files[key] : '';
    };

    const originalContent = findContent(previewFiles, tab);

    const matchingChange = proposedChanges.find(c => {
        const exts = extsByTab[tab] || [];
        return exts.some(ext => c.file_path.endsWith(ext));
    });

    const newContent = matchingChange?.full_content ?? originalContent;

    if (showDiff && matchingChange) {
        return (
            <ReactDiffViewer
                oldValue={originalContent}
                newValue={newContent}
                splitView={false}
                compareMethod={DiffMethod.WORDS}
                showDiffOnly={false}
                useDarkTheme={false}
                styles={{
                    variables: {
                        light: {
                            addedBackground: 'rgba(46,160,67,0.15)',
                            removedBackground: 'rgba(248,81,73,0.15)',
                            gutterBackground: '#f3f3f3',
                        },
                    },
                    line: { fontFamily: '"Cascadia Code","Fira Code","Consolas",monospace', fontSize: '12px' },
                }}
            />
        );
    }

    const displayContent = showDiff ? newContent : originalContent;
    if (!displayContent) {
        return (
            <div style={{ padding: '16px', fontSize: '12px', color: IDE_TEXT_DIM, fontStyle: 'italic' }}>
                No {tab.toUpperCase()} file found for this form.
            </div>
        );
    }

    return (
        <pre style={{
            margin: 0, padding: '12px 0', fontSize: '12px', lineHeight: '18px',
            fontFamily: '"Cascadia Code","Fira Code","Consolas",monospace',
            whiteSpace: 'pre', overflowX: 'auto', backgroundColor: '#fafafa',
        }}>
            {displayContent.split('\n').map((line, i) => (
                <div key={i} style={{ display: 'flex', minWidth: 'max-content' }}>
                    <span style={{ display: 'inline-block', width: '44px', textAlign: 'right', paddingRight: '12px', color: '#aaa', userSelect: 'none', flexShrink: 0 }}>
                        {i + 1}
                    </span>
                    <span style={{ paddingRight: '24px' }}>{line}</span>
                </div>
            ))}
        </pre>
    );
};

// ── FeedbackBar sub-component ─────────────────────────────────────────────────

/**
 * Bottom bar that changes behaviour based on the current view:
 * - 'preview': shows feedback textarea + type selector + Analyze & Enhance button
 * - 'enhancedPreview': shows commit message input + selected file count + Push to ADO button
 */
const FeedbackBar = ({
    view, feedback, setFeedback, feedbackType, setFeedbackType,
    feedbackTypes, loading, proposedChanges, selectedChanges,
    commitMessage, setCommitMessage, onEnhance, onConfirmAndPush, onRefine,
}) => {
    const isEnhanced = view === 'enhancedPreview';
    const selectedCount = Object.values(selectedChanges).filter(Boolean).length;

    return (
        <div style={{ flexShrink: 0, borderTop: `1px solid ${IDE_BORDER}`, backgroundColor: '#ffffff', padding: '10px 16px' }}>
            {!isEnhanced ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <select
                        value={feedbackType}
                        onChange={e => setFeedbackType(e.target.value)}
                        style={{
                            padding: '7px 8px', fontSize: '12px', border: `1px solid ${IDE_BORDER}`,
                            borderRadius: '4px', color: IDE_TEXT, flexShrink: 0, backgroundColor: '#ffffff',
                        }}
                    >
                        {feedbackTypes.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                    <textarea
                        value={feedback}
                        onChange={e => setFeedback(e.target.value)}
                        placeholder="Describe the change you need while viewing the preview above (e.g. 'Move the Save button to the top right')..."
                        rows={2}
                        style={{
                            flex: 1, padding: '7px 10px', fontSize: '13px', resize: 'none',
                            border: `1px solid ${IDE_BORDER}`, borderRadius: '4px',
                            color: IDE_TEXT, fontFamily: 'inherit', lineHeight: 1.5,
                            backgroundColor: '#f5f5f5',
                        }}
                    />
                    <button
                        onClick={onEnhance}
                        disabled={loading || !feedback.trim()}
                        style={{
                            alignSelf: 'stretch', padding: '0 20px', fontSize: '13px', fontWeight: 600,
                            backgroundColor: (!feedback.trim() || loading) ? '#e0e0e0' : IDE_ACCENT,
                            color: (!feedback.trim() || loading) ? IDE_TEXT_DIM : 'white',
                            border: 'none', borderRadius: '4px',
                            cursor: (!feedback.trim() || loading) ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {loading ? 'Enhancing...' : 'Analyze & Enhance →'}
                    </button>
                </div>
            ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                        onClick={() => window.dispatchEvent(new CustomEvent('ff-back-to-preview'))}
                        style={{ padding: '7px 12px', fontSize: '13px', backgroundColor: 'transparent', border: `1px solid ${IDE_BORDER}`, color: IDE_TEXT, borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                        ← Revise
                    </button>
                    <button
                        onClick={onRefine}
                        style={{ padding: '7px 14px', fontSize: '13px', backgroundColor: 'transparent', border: `1px solid ${IDE_ACCENT}`, color: IDE_ACCENT, borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                        Refine Feedback ↺
                    </button>
                    <input
                        type="text"
                        value={commitMessage}
                        onChange={e => setCommitMessage(e.target.value)}
                        placeholder="Commit message (optional)..."
                        style={{
                            flex: 1, padding: '7px 12px', fontSize: '13px',
                            backgroundColor: '#f5f5f5', border: `1px solid ${IDE_BORDER}`,
                            color: IDE_TEXT, borderRadius: '4px', outline: 'none',
                        }}
                    />
                    <span style={{ fontSize: '12px', color: IDE_TEXT_DIM, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {selectedCount} of {proposedChanges.length} file{proposedChanges.length !== 1 ? 's' : ''}
                    </span>
                    <button
                        onClick={onConfirmAndPush}
                        disabled={loading || selectedCount === 0}
                        style={{
                            padding: '7px 22px', fontSize: '13px', fontWeight: 600,
                            backgroundColor: (loading || selectedCount === 0) ? '#e0e0e0' : IDE_ACCENT,
                            color: (loading || selectedCount === 0) ? IDE_TEXT_DIM : 'white',
                            border: 'none', borderRadius: '4px',
                            cursor: (loading || selectedCount === 0) ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                    >
                        {loading ? 'Pushing...' : 'Confirm & Push to ADO'}
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Main FormFeedback component ───────────────────────────────────────────────

const FormFeedback = ({ isOpen, onClose }) => {
    const [view, setView] = useState('input'); // 'input' | 'preview' | 'processing' | 'enhancedPreview' | 'commit' | 'filePreview' | 'changes'
    const [formName, setFormName] = useState('');
    const [feedback, setFeedback] = useState('');
    const [feedbackType, setFeedbackType] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Form selection
    const [availableForms, setAvailableForms] = useState([]);
    const [fileTree, setFileTree] = useState(null);
    const [loadingForms, setLoadingForms] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [backendExpanded, setBackendExpanded] = useState(true);
    const [frontendExpanded, setFrontendExpanded] = useState(true);

    // Analysis results
    const [analysis, setAnalysis] = useState(null);
    const [contextInfo, setContextInfo] = useState(null);
    const [affectedFiles, setAffectedFiles] = useState([]);

    // Proposed changes
    const [proposedChanges, setProposedChanges] = useState([]);
    const [selectedChanges, setSelectedChanges] = useState({});
    const [activeChangeIndex, setActiveChangeIndex] = useState(0);
    const [commitMessage, setCommitMessage] = useState('');
    const [feedbackId, setFeedbackId] = useState(null); // eslint-disable-line no-unused-vars
    const [viewMode, setViewMode] = useState('code'); // 'code' | 'diff'

    // Live preview state
    const [previewFiles, setPreviewFiles] = useState({});
    const [previewSrcDoc, setPreviewSrcDoc] = useState('');
    const [enhancedSrcDoc, setEnhancedSrcDoc] = useState('');
    const [activePreviewTab, setActivePreviewTab] = useState('html');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [currentContents, setCurrentContents] = useState({}); // ADO path → latest content for iterative refinement

    // Oracle Form screenshot state
    const [formScreenshot, setFormScreenshot] = useState(null);       // base64 string
    const [screenshotFormat, setScreenshotFormat] = useState(null);   // 'gif', 'png', etc.
    const [screenshotLoading, setScreenshotLoading] = useState(false);
    const [screenshotCache, setScreenshotCache] = useState({});       // { formName: {screenshot, format} | null }
    const [showOracleModal, setShowOracleModal] = useState(false);

    const feedbackTypes = [
        { value: '', label: 'Auto-detect' },
        { value: 'bug_fix', label: 'Bug Fix' },
        { value: 'enhancement', label: 'Enhancement' },
        { value: 'validation', label: 'Validation' },
        { value: 'ui_change', label: 'UI Change' },
        { value: 'business_logic', label: 'Business Logic' },
        { value: 'performance', label: 'Performance' },
        { value: 'refactoring', label: 'Refactoring' },
    ];

    React.useEffect(() => {
        if (isOpen && availableForms.length === 0) {
            loadForms();
        }
    }, [isOpen]);

    // Listen for "Revise" back-navigation from FeedbackBar
    React.useEffect(() => {
        const handler = () => setView('preview');
        window.addEventListener('ff-back-to-preview', handler);
        return () => window.removeEventListener('ff-back-to-preview', handler);
    }, []);

    const loadForms = async () => {
        setLoadingForms(true);
        setError(null);
        try {
            const result = await api.artifacts.listForms();
            setAvailableForms(result.forms || []);
        } catch (err) {
            setError(`Failed to load forms: ${err.message}`);
        } finally {
            setLoadingForms(false);
        }
    };

    const handleFormSelect = async (e) => {
        const selectedForm = e.target.value;
        setFormName(selectedForm);
        setFileTree(null);
        setError(null);
        setPreviewFiles({});
        setPreviewSrcDoc('');
        setEnhancedSrcDoc('');
        setFormScreenshot(null);
        setScreenshotFormat(null);
        if (selectedForm) {
            setLoadingFiles(true);
            try {
                const files = await api.artifacts.getFormFiles(selectedForm);
                setFileTree(files);
            } catch (err) {
                setError(`Failed to load files: ${err.message}`);
            } finally {
                setLoadingFiles(false);
            }
        }
    };

    const handleClose = () => {
        setView('input');
        setFormName('');
        setFeedback('');
        setFeedbackType('');
        setError(null);
        setSuccessMessage(null);
        setAnalysis(null);
        setContextInfo(null);
        setAffectedFiles([]);
        setFileTree(null);
        setProposedChanges([]);
        setSelectedChanges({});
        setActiveChangeIndex(0);
        setCommitMessage('');
        setFeedbackId(null);
        setPreviewFiles({});
        setPreviewSrcDoc('');
        setEnhancedSrcDoc('');
        setFormScreenshot(null);
        setScreenshotFormat(null);
        setScreenshotLoading(false);
        setShowOracleModal(false);
        setActivePreviewTab('html');
        setPreviewLoading(false);
        onClose();
    };

    // ── New: load frontend files and render preview ──────────────────────────
    const handleLoadPreview = async () => {
        if (!formName || !fileTree) return;
        setPreviewLoading(true);
        setError(null);
        try {
            // getFrontendFile expects paths relative to the frontend root (e.g. 'angular/ebal008/...')
            // fileTree.frontend.path is the full path ('generated/frontend/angular/ebal008')
            // so we strip the 'generated/frontend/' prefix
            const relPath = getRelativeFrontendPath(fileTree.frontend.path || '');

            // Only fetch the component HTML + CSS/SCSS files needed for the iframe preview
            const componentFiles = (fileTree?.frontend?.files || []).filter(f =>
                f.endsWith('.component.html') ||
                f.endsWith('.component.scss') ||
                f.endsWith('.component.css')
            );

            if (!componentFiles.length) {
                setError('No component HTML/CSS files found for this form');
                return;
            }

            const paths = componentFiles.map(f => `${relPath}/${f}`);
            const [fileMap] = await Promise.all([
                api.artifacts.getMultipleFrontendFiles(paths),
                loadFormScreenshot(formName),
            ]);
            setPreviewFiles(fileMap);
            setPreviewSrcDoc(buildPreviewSrcDoc(fileMap, formName));
            setActivePreviewTab('html');
            setView('preview');
        } catch (err) {
            setError(`Failed to load preview: ${err.message}`);
        } finally {
            setPreviewLoading(false);
        }
    };

    // ── Load Oracle Form screenshot from source ZIP ──────────────────────────
    const loadFormScreenshot = async (name) => {
        if (screenshotCache.hasOwnProperty(name)) {
            const cached = screenshotCache[name];
            setFormScreenshot(cached?.screenshot || null);
            setScreenshotFormat(cached?.format || null);
            return;
        }
        setScreenshotLoading(true);
        try {
            const result = await api.artifacts.getFormScreenshot(name);
            if (result.found && result.screenshot) {
                setFormScreenshot(result.screenshot);
                setScreenshotFormat(result.format || 'png');
                setScreenshotCache(prev => ({ ...prev, [name]: { screenshot: result.screenshot, format: result.format || 'png' } }));
            } else {
                setFormScreenshot(null);
                setScreenshotFormat(null);
                setScreenshotCache(prev => ({ ...prev, [name]: null }));
            }
        } catch (err) {
            console.warn('Failed to load form screenshot:', err.message);
            setFormScreenshot(null);
            setScreenshotCache(prev => ({ ...prev, [name]: null }));
        } finally {
            setScreenshotLoading(false);
        }
    };

    // ── New: analyze feedback + process + build enhanced preview ─────────────
    const handleEnhance = async () => {
        if (!feedback.trim()) {
            setError('Please enter feedback before enhancing');
            return;
        }
        setLoading(true);
        setView('processing');
        setError(null);
        try {
            const [analysisResult, contextResult] = await Promise.all([
                api.artifacts.analyzeFeedback(formName.trim(), feedback.trim()),
                api.artifacts.getFormContext(formName.trim(), feedback.trim()),
            ]);
            setAnalysis(analysisResult.analysis);
            setContextInfo(contextResult);
            setAffectedFiles(analysisResult.affected_files || []);

            const allPaths = [
                ...(fileTree?.backend?.files || []).map(f => `${fileTree.backend.path}/${f}`),
                ...(fileTree?.frontend?.files || []).map(f => `${fileTree.frontend.path}/${f}`),
            ];
            const result = await api.artifacts.processFeedback(
                formName, feedback.trim(), feedbackType || null, allPaths, currentContents
            );
            setFeedbackId(result.feedback_id);
            const changes = result.proposed_changes || [];
            setProposedChanges(changes);
            const selected = {};
            changes.forEach((_, i) => { selected[i] = true; });
            setSelectedChanges(selected);

            // Merge AI changes into previewFiles to build the enhanced srcdoc
            const merged = { ...previewFiles };
            changes.forEach(change => {
                const key = Object.keys(merged).find(
                    k => k === change.file_path || k.endsWith(change.file_path.split('/').pop())
                );
                if (key) merged[key] = change.full_content;
            });
            setEnhancedSrcDoc(buildPreviewSrcDoc(merged, formName));
            setView('enhancedPreview');
        } catch (err) {
            setError(err.message || 'Enhancement failed');
            setView('preview');
        } finally {
            setLoading(false);
        }
    };

    // ── Iterative refinement ──────────────────────────────────────────────────
    const handleRefine = () => {
        // 1. Accumulate proposed changes into currentContents for next round
        const updated = { ...currentContents };
        proposedChanges.forEach(change => {
            updated[change.file_path] = change.full_content;
        });
        setCurrentContents(updated);

        // 2. Apply changes to previewFiles so BEFORE preview shows the enhanced code
        const merged = { ...previewFiles };
        proposedChanges.forEach(change => {
            const key = Object.keys(merged).find(
                k => k === change.file_path || k.endsWith(change.file_path.split('/').pop())
            );
            if (key) merged[key] = change.full_content;
        });
        setPreviewFiles(merged);
        setPreviewSrcDoc(buildPreviewSrcDoc(merged, formName));

        // 3. Reset per-round state
        setFeedback('');
        setProposedChanges([]);
        setSelectedChanges({});
        setCommitMessage('');

        // 4. Return to preview for next round of feedback
        setView('preview');
    };

    // ── Existing handlers kept intact ────────────────────────────────────────
    const handleAnalyze = async () => {
        if (!formName.trim() || !feedback.trim()) {
            setError('Please enter both form name and feedback');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [analysisResult, contextResult] = await Promise.all([
                api.artifacts.analyzeFeedback(formName.trim(), feedback.trim()),
                api.artifacts.getFormContext(formName.trim(), feedback.trim())
            ]);
            setAnalysis(analysisResult.analysis);
            setContextInfo(contextResult);
            if (analysisResult.affected_files) {
                setAffectedFiles(analysisResult.affected_files);
            }
            setView('filePreview');
        } catch (err) {
            setError(err.message || 'Analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessFeedback = async () => {
        setLoading(true);
        setError(null);
        try {
            const allFilePaths = [];
            if (fileTree) {
                if (fileTree.backend && fileTree.backend.files && fileTree.backend.path) {
                    fileTree.backend.files.forEach(file => {
                        allFilePaths.push(`${fileTree.backend.path}/${file}`);
                    });
                }
                if (fileTree.frontend && fileTree.frontend.files && fileTree.frontend.path) {
                    fileTree.frontend.files.forEach(file => {
                        allFilePaths.push(`${fileTree.frontend.path}/${file}`);
                    });
                }
            }
            const result = await api.artifacts.processFeedback(
                formName.trim(),
                feedback.trim(),
                feedbackType || null,
                allFilePaths
            );
            setFeedbackId(result.feedback_id);
            setProposedChanges(result.proposed_changes || []);
            const selected = {};
            (result.proposed_changes || []).forEach((_, idx) => { selected[idx] = true; });
            setSelectedChanges(selected);
            setView('changes');
        } catch (err) {
            setError(err.message || 'Processing failed');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleChange = (index) => {
        setSelectedChanges(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleCommitChanges = async () => {
        const changesToCommit = proposedChanges.filter((_, idx) => selectedChanges[idx]);
        if (changesToCommit.length === 0) {
            setError('Please select at least one change to commit');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const changes = changesToCommit.map(change => ({
                path: change.file_path,
                content: change.full_content
            }));
            await api.artifacts.commitMultipleFiles(
                changes,
                commitMessage || `Form feedback: ${feedback.substring(0, 50)}...`
            );
            try {
                await api.artifacts.storeFeedback(
                    formName, feedback,
                    `Applied ${changesToCommit.length} changes in single commit`,
                    changesToCommit.map(c => c.file_path),
                    analysis?.feedback_type || 'general'
                );
            } catch (storeErr) {
                console.warn('Could not store feedback for learning:', storeErr);
            }
            setSuccessMessage(`Successfully committed ${changesToCommit.length} change(s)`);
            setView('commit');
        } catch (err) {
            setError(err.message || 'Commit failed');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmAndPush = async () => {
        await handleCommitChanges();
    };

    if (!isOpen) return null;

    const activeChange = proposedChanges[activeChangeIndex];

    const getFileIcon = (filename) => {
        const ext = filename?.split('.').pop()?.toLowerCase();
        const colorMap = {
            ts: '#3178c6', js: '#f7df1e', html: '#e34c26',
            scss: '#c76494', css: '#264de4', py: '#3572a5',
            json: '#cbcb41', md: '#519aba',
        };
        return colorMap[ext] || '#cccccc';
    };

    const getChangedFileIndex = (filePath) => {
        return proposedChanges.findIndex(c => c.file_path === filePath || c.file_path.endsWith(filePath) || filePath.endsWith(c.file_path.split('/').pop()));
    };

    const handleSidebarFileClick = async (file) => {
        if (view === 'changes') {
            const changedIdx = getChangedFileIndex(file);
            if (changedIdx !== -1) setActiveChangeIndex(changedIdx);
        }
        if (view === 'preview' || view === 'enhancedPreview') {
            const ext = file.split('.').pop().toLowerCase();
            if (ext === 'html') setActivePreviewTab('html');
            else if (ext === 'css' || ext === 'scss') setActivePreviewTab('css');
            else if (ext === 'ts') setActivePreviewTab('ts');

            // Fetch file content on demand if not already loaded
            const relPath = getRelativeFrontendPath(fileTree?.frontend?.path || '');
            const fetchPath = `${relPath}/${file}`;
            if (!previewFiles[fetchPath]) {
                try {
                    const data = await api.artifacts.getFrontendFile(fetchPath);
                    const content = data?.content ?? '';
                    setPreviewFiles(prev => ({ ...prev, [fetchPath]: content }));
                } catch (err) {
                    console.warn(`Could not fetch ${file}:`, err.message);
                }
            }
        }
    };

    const steps = [
        { id: 'input', label: 'Select' },
        { id: 'preview', label: 'Preview' },
        { id: 'processing', label: 'Enhance' },
        { id: 'enhancedPreview', label: 'Review' },
        { id: 'commit', label: 'Done' },
    ];
    const currentStepIdx = steps.findIndex(s => s.id === view);

    return (
        <div className="fixed inset-x-0 top-0 z-50 flex flex-col" style={{ bottom: '40px', backgroundColor: IDE_BG, color: IDE_TEXT, fontFamily: '"Segoe UI", system-ui, sans-serif' }}>

            {/* ── Title bar ── */}
            <div className="flex items-center justify-between px-4 select-none" style={{ backgroundColor: IDE_TITLE, borderBottom: `1px solid ${IDE_BORDER}`, height: '40px', minHeight: '40px' }}>
                <div className="flex items-center gap-2">
                    <IoGitCommit style={{ color: IDE_ACCENT, width: 18, height: 18 }} />
                    <span style={{ fontSize: '13px', color: IDE_TEXT }}>Form Feedback IDE</span>
                    {formName && (
                        <span style={{ fontSize: '13px', color: IDE_TEXT_DIM }}>— {formName}</span>
                    )}
                </div>

                {/* Step breadcrumb */}
                <div className="flex items-center gap-1" style={{ fontSize: '12px' }}>
                    {steps.map((step, idx) => (
                        <React.Fragment key={step.id}>
                            <span style={{
                                color: idx === currentStepIdx ? IDE_ACCENT : idx < currentStepIdx ? IDE_GREEN : IDE_TEXT_DIM,
                                fontWeight: idx === currentStepIdx ? 600 : 400,
                            }}>
                                {idx < currentStepIdx ? '✓ ' : ''}{step.label}
                            </span>
                            {idx < steps.length - 1 && <span style={{ color: IDE_TEXT_DIM, margin: '0 4px' }}>›</span>}
                        </React.Fragment>
                    ))}
                </div>

                <button
                    onClick={handleClose}
                    style={{ color: IDE_TEXT_DIM, background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#c0392b'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <IoClose style={{ width: 18, height: 18 }} />
                </button>
            </div>

            {/* ── Main area ── */}
            <div className="flex flex-1 min-h-0">

                {/* ── LEFT SIDEBAR ── */}
                <div className="flex flex-col" style={{ width: '260px', minWidth: '260px', backgroundColor: IDE_SIDEBAR, borderRight: `1px solid ${IDE_BORDER}`, overflow: 'hidden' }}>

                    <div style={{ padding: '8px 12px 4px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: IDE_TEXT_DIM, textTransform: 'uppercase', borderBottom: `1px solid ${IDE_BORDER}` }}>
                        Explorer
                    </div>

                    {/* Form selector */}
                    <div style={{ padding: '8px 10px', borderBottom: `1px solid ${IDE_BORDER}` }}>
                        {loadingForms ? (
                            <div style={{ fontSize: '12px', color: IDE_TEXT_DIM }}>Loading forms...</div>
                        ) : (
                            <select
                                value={formName}
                                onChange={handleFormSelect}
                                style={{
                                    width: '100%', padding: '4px 6px', fontSize: '12px',
                                    backgroundColor: '#ffffff', border: `1px solid ${IDE_BORDER}`,
                                    color: IDE_TEXT, borderRadius: '3px', outline: 'none', cursor: 'pointer'
                                }}
                            >
                                <option value="">— Select Form —</option>
                                {availableForms.map(form => (
                                    <option key={form.name} value={form.name}>
                                        {form.name}{form.status === 'partial' ? ' (Partial)' : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Preview Form button — shown on input view once form + file tree are loaded */}
                    {view === 'input' && formName && fileTree && (
                        <div style={{ padding: '6px 10px', borderBottom: `1px solid ${IDE_BORDER}` }}>
                            <button
                                onClick={handleLoadPreview}
                                disabled={previewLoading}
                                style={{
                                    width: '100%', padding: '6px', fontSize: '12px', fontWeight: 600,
                                    backgroundColor: previewLoading ? '#e0e0e0' : IDE_ACCENT,
                                    color: previewLoading ? IDE_TEXT_DIM : 'white',
                                    border: 'none', borderRadius: '3px',
                                    cursor: previewLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {previewLoading ? 'Loading preview...' : 'Preview Form →'}
                            </button>
                        </div>
                    )}

                    {/* File tree */}
                    <div className="flex-1 overflow-y-auto" style={{ fontSize: '13px' }}>
                        {loadingFiles && (
                            <div style={{ padding: '12px', color: IDE_TEXT_DIM, fontSize: '12px' }}>Loading files...</div>
                        )}

                        {fileTree && (
                            <>
                                {/* Backend section */}
                                {fileTree.backend && fileTree.backend.count > 0 && (
                                    <div>
                                        <button
                                            onClick={() => setBackendExpanded(!backendExpanded)}
                                            style={{
                                                width: '100%', display: 'flex', alignItems: 'center', gap: '4px',
                                                padding: '4px 8px', background: 'none', border: 'none',
                                                color: '#c586c0', cursor: 'pointer', fontSize: '12px',
                                                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em'
                                            }}
                                        >
                                            {backendExpanded ? <IoChevronDown style={{ width: 14, height: 14 }} /> : <IoChevronForward style={{ width: 14, height: 14 }} />}
                                            <span>Backend</span>
                                            <span style={{ marginLeft: 'auto', background: '#8b5cf6', color: 'white', borderRadius: '10px', padding: '0 6px', fontSize: '10px' }}>
                                                {fileTree.backend.count}
                                            </span>
                                        </button>
                                        {backendExpanded && fileTree.backend.files.map((file, idx) => {
                                            const changedIdx = view === 'changes' ? getChangedFileIndex(file) : -1;
                                            const isActive = view === 'changes' && changedIdx === activeChangeIndex;
                                            const isChanged = changedIdx !== -1;
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleSidebarFileClick(file)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        padding: '3px 8px 3px 24px',
                                                        backgroundColor: isActive ? IDE_ACTIVE : 'transparent',
                                                        cursor: 'pointer',
                                                        borderLeft: isActive ? `2px solid ${IDE_ACCENT}` : '2px solid transparent',
                                                    }}
                                                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = IDE_SIDEBAR_HOVER; }}
                                                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    <IoDocument style={{ width: 14, height: 14, flexShrink: 0, color: getFileIcon(file) }} />
                                                    <span style={{
                                                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        fontSize: '13px', color: isActive ? 'white' : IDE_TEXT
                                                    }} title={file}>
                                                        {file.split('/').pop()}
                                                    </span>
                                                    {isChanged && (
                                                        <span style={{ color: '#f59e0b', fontSize: '10px', flexShrink: 0 }}>M</span>
                                                    )}
                                                    {isChanged && !selectedChanges[changedIdx] && (
                                                        <span style={{ color: IDE_TEXT_DIM, fontSize: '10px', flexShrink: 0 }}>—</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Frontend section */}
                                {fileTree.frontend && fileTree.frontend.count > 0 && (
                                    <div>
                                        <button
                                            onClick={() => setFrontendExpanded(!frontendExpanded)}
                                            style={{
                                                width: '100%', display: 'flex', alignItems: 'center', gap: '4px',
                                                padding: '4px 8px', background: 'none', border: 'none',
                                                color: '#4ec9b0', cursor: 'pointer', fontSize: '12px',
                                                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em'
                                            }}
                                        >
                                            {frontendExpanded ? <IoChevronDown style={{ width: 14, height: 14 }} /> : <IoChevronForward style={{ width: 14, height: 14 }} />}
                                            <span>Frontend</span>
                                            <span style={{ marginLeft: 'auto', background: '#3b82f6', color: 'white', borderRadius: '10px', padding: '0 6px', fontSize: '10px' }}>
                                                {fileTree.frontend.count}
                                            </span>
                                        </button>
                                        {frontendExpanded && fileTree.frontend.files.map((file, idx) => {
                                            const changedIdx = view === 'changes' ? getChangedFileIndex(file) : -1;
                                            const isActive = view === 'changes' && changedIdx === activeChangeIndex;
                                            const isChanged = changedIdx !== -1;
                                            // Highlight when this tab is active in preview views
                                            const isPreviewActive = (view === 'preview' || view === 'enhancedPreview') && (() => {
                                                const ext = file.split('.').pop().toLowerCase();
                                                return (activePreviewTab === 'html' && ext === 'html') ||
                                                    (activePreviewTab === 'css' && (ext === 'css' || ext === 'scss')) ||
                                                    (activePreviewTab === 'ts' && ext === 'ts');
                                            })();
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleSidebarFileClick(file)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        padding: '3px 8px 3px 24px',
                                                        backgroundColor: isActive || isPreviewActive ? IDE_ACTIVE : 'transparent',
                                                        cursor: 'pointer',
                                                        borderLeft: (isActive || isPreviewActive) ? `2px solid ${IDE_ACCENT}` : '2px solid transparent',
                                                    }}
                                                    onMouseEnter={e => { if (!isActive && !isPreviewActive) e.currentTarget.style.backgroundColor = IDE_SIDEBAR_HOVER; }}
                                                    onMouseLeave={e => { if (!isActive && !isPreviewActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    <IoDocument style={{ width: 14, height: 14, flexShrink: 0, color: getFileIcon(file) }} />
                                                    <span style={{
                                                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        fontSize: '13px', color: (isActive || isPreviewActive) ? IDE_ACCENT : IDE_TEXT
                                                    }} title={file}>
                                                        {file.split('/').pop()}
                                                    </span>
                                                    {isChanged && (
                                                        <span style={{ color: '#f59e0b', fontSize: '10px', flexShrink: 0 }}>M</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Changed files list (changes view) */}
                                {view === 'changes' && proposedChanges.length > 0 && (
                                    <div style={{ marginTop: '8px', borderTop: `1px solid ${IDE_BORDER}`, paddingTop: '4px' }}>
                                        <div style={{ padding: '4px 8px', fontSize: '11px', fontWeight: 700, color: IDE_TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                            Changed ({proposedChanges.length})
                                        </div>
                                        {proposedChanges.map((change, idx) => {
                                            const fileName = change.file_path.split('/').pop();
                                            const isActive = idx === activeChangeIndex;
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => setActiveChangeIndex(idx)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        padding: '4px 8px 4px 16px',
                                                        backgroundColor: isActive ? IDE_ACTIVE : 'transparent',
                                                        cursor: 'pointer',
                                                        borderLeft: isActive ? `2px solid ${IDE_ACCENT}` : '2px solid transparent',
                                                    }}
                                                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = IDE_SIDEBAR_HOVER; }}
                                                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedChanges[idx] || false}
                                                        onChange={() => handleToggleChange(idx)}
                                                        onClick={e => e.stopPropagation()}
                                                        style={{ accentColor: IDE_ACCENT, flexShrink: 0, cursor: 'pointer' }}
                                                    />
                                                    <IoDocument style={{ width: 13, height: 13, flexShrink: 0, color: '#f59e0b' }} />
                                                    <span style={{
                                                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        fontSize: '12px', color: isActive ? IDE_ACCENT : '#ca8a04'
                                                    }} title={change.file_path}>
                                                        {fileName}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {!fileTree && !loadingFiles && formName && (
                            <div style={{ padding: '12px', color: IDE_TEXT_DIM, fontSize: '12px' }}>No files found</div>
                        )}
                        {!formName && (
                            <div style={{ padding: '12px', color: IDE_TEXT_DIM, fontSize: '12px', fontStyle: 'italic' }}>Select a form to browse files</div>
                        )}
                    </div>

                    {/* Oracle Form Screenshot Thumbnail */}
                    {formScreenshot && (
                        <div style={{
                            padding: '8px 10px', borderTop: `1px solid ${IDE_BORDER}`,
                        }}>
                            <div style={{
                                fontSize: '11px', fontWeight: 700, color: IDE_TEXT_DIM,
                                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px',
                            }}>
                                Original Oracle Form
                            </div>
                            <img
                                src={`data:image/${screenshotFormat || 'png'};base64,${formScreenshot}`}
                                alt={`Oracle Form: ${formName}`}
                                style={{
                                    width: '100%', borderRadius: '4px',
                                    border: `1px solid ${IDE_BORDER}`, cursor: 'pointer',
                                }}
                                title="Click to enlarge"
                                onClick={() => setShowOracleModal(true)}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    {screenshotLoading && (
                        <div style={{ padding: '8px 10px', fontSize: '11px', color: IDE_TEXT_DIM }}>
                            Loading form image...
                        </div>
                    )}
                </div>

                {/* ── RIGHT PANEL ── */}
                <div className="flex flex-1 flex-col min-h-0">

                    {/* Tab bar (changes view) */}
                    {view === 'changes' && activeChange && (
                        <div style={{ display: 'flex', alignItems: 'center', backgroundColor: IDE_SIDEBAR, borderBottom: `1px solid ${IDE_BORDER}`, minHeight: '35px' }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '0 16px', height: '35px',
                                borderRight: `1px solid ${IDE_BORDER}`,
                                borderBottom: `2px solid ${IDE_ACCENT}`,
                                backgroundColor: IDE_BG,
                                fontSize: '13px', color: IDE_TEXT
                            }}>
                                <IoDocument style={{ width: 13, height: 13, color: getFileIcon(activeChange.file_path) }} />
                                <span>{activeChange.file_path.split('/').pop()}</span>
                            </div>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', padding: '0 12px' }}>
                                {['code', 'diff'].map(mode => (
                                    <button
                                        key={mode}
                                        onClick={() => setViewMode(mode)}
                                        style={{
                                            padding: '3px 10px', fontSize: '11px', borderRadius: '3px', border: 'none',
                                            cursor: 'pointer', fontWeight: viewMode === mode ? 600 : 400,
                                            backgroundColor: viewMode === mode ? IDE_ACCENT : 'transparent',
                                            color: viewMode === mode ? 'white' : IDE_TEXT_DIM,
                                            textTransform: 'uppercase', letterSpacing: '0.04em'
                                        }}
                                    >
                                        {mode === 'code' ? 'Full Code' : 'Diff'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Error / success banners */}
                    {(error || successMessage) && (
                        <div style={{
                            padding: '8px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
                            backgroundColor: error ? 'rgba(220,38,38,0.15)' : 'rgba(34,197,94,0.15)',
                            color: error ? '#f87171' : '#4ade80',
                            borderBottom: `1px solid ${IDE_BORDER}`
                        }}>
                            {error ? <IoAlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} /> : <IoCheckmarkCircle style={{ width: 16, height: 16, flexShrink: 0 }} />}
                            {error || successMessage}
                            <button onClick={() => { setError(null); setSuccessMessage(null); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '16px' }}>×</button>
                        </div>
                    )}

                    {/* ── INPUT VIEW ── */}
                    {view === 'input' && (
                        <div className="flex-1 overflow-y-auto" style={{ padding: '32px 48px', backgroundColor: IDE_BG }}>
                            <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                                <div style={{ marginBottom: '24px' }}>
                                    <h2 style={{ fontSize: '20px', fontWeight: 600, color: IDE_TEXT, marginBottom: '6px' }}>Form Feedback</h2>
                                    <p style={{ fontSize: '13px', color: IDE_TEXT_DIM, lineHeight: 1.6 }}>
                                        Select a form to load a live preview of the generated Angular code. View the UI, then provide feedback to enhance it.
                                    </p>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: IDE_TEXT_DIM, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Feedback Type
                                        </label>
                                        <select
                                            value={feedbackType}
                                            onChange={(e) => setFeedbackType(e.target.value)}
                                            style={{ width: '100%', padding: '8px 10px', fontSize: '13px', backgroundColor: '#ffffff', border: `1px solid ${IDE_BORDER}`, color: IDE_TEXT, borderRadius: '4px', outline: 'none' }}
                                        >
                                            {feedbackTypes.map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: IDE_TEXT_DIM, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Your Feedback *
                                        </label>
                                        <textarea
                                            value={feedback}
                                            onChange={(e) => setFeedback(e.target.value)}
                                            placeholder="Describe the changes you need..."
                                            rows={8}
                                            style={{
                                                width: '100%', padding: '10px 12px', fontSize: '13px',
                                                backgroundColor: '#f5f5f5', border: `1px solid ${IDE_BORDER}`,
                                                color: IDE_TEXT, borderRadius: '4px', outline: 'none',
                                                resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6
                                            }}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        {formName && fileTree && (
                                            <button
                                                onClick={handleLoadPreview}
                                                disabled={previewLoading}
                                                style={{
                                                    padding: '9px 24px', fontSize: '13px', fontWeight: 600,
                                                    backgroundColor: previewLoading ? '#e0e0e0' : IDE_ACCENT,
                                                    color: previewLoading ? IDE_TEXT_DIM : 'white',
                                                    border: 'none', borderRadius: '4px',
                                                    cursor: previewLoading ? 'not-allowed' : 'pointer',
                                                }}
                                            >
                                                {previewLoading ? 'Loading...' : 'Preview Form →'}
                                            </button>
                                        )}
                                        <button
                                            onClick={handleAnalyze}
                                            disabled={loading || !formName.trim() || !feedback.trim()}
                                            style={{
                                                padding: '9px 24px', fontSize: '13px', fontWeight: 600,
                                                backgroundColor: (!formName.trim() || !feedback.trim() || loading) ? '#e0e0e0' : '#6b7280',
                                                color: (!formName.trim() || !feedback.trim() || loading) ? IDE_TEXT_DIM : 'white',
                                                border: 'none', borderRadius: '4px', cursor: (!formName.trim() || !feedback.trim() || loading) ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            {loading ? 'Analyzing...' : 'Analyze Only →'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── PREVIEW VIEW ── */}
                    {view === 'preview' && (
                        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>

                            {/* Top bar: HTML/CSS/TS tabs + Back button */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '6px 12px', backgroundColor: IDE_SIDEBAR,
                                borderBottom: `1px solid ${IDE_BORDER}`, minHeight: '36px', flexShrink: 0,
                            }}>
                                <span style={{ fontSize: '11px', color: IDE_TEXT_DIM, marginRight: '2px' }}>Code:</span>
                                {['html', 'css', 'ts'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActivePreviewTab(tab)}
                                        style={{
                                            padding: '3px 10px', fontSize: '11px', borderRadius: '3px', border: 'none',
                                            cursor: 'pointer', textTransform: 'uppercase',
                                            backgroundColor: activePreviewTab === tab ? IDE_ACCENT : 'transparent',
                                            color: activePreviewTab === tab ? 'white' : IDE_TEXT_DIM,
                                            fontWeight: activePreviewTab === tab ? 600 : 400,
                                        }}
                                    >
                                        {tab}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setView('input')}
                                    style={{
                                        marginLeft: 'auto', padding: '3px 10px', fontSize: '11px',
                                        backgroundColor: 'transparent', border: `1px solid ${IDE_BORDER}`,
                                        color: IDE_TEXT_DIM, borderRadius: '3px', cursor: 'pointer',
                                    }}
                                >
                                    ← Back
                                </button>
                            </div>

                            {/* Preview iframe (55%) + Code panel (45%) */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                <div style={{ flex: '0 0 55%', display: 'flex', borderBottom: `1px solid ${IDE_BORDER}`, overflow: 'hidden' }}>
                                    {/* Original Oracle Form screenshot (side-by-side) */}
                                    {formScreenshot && (
                                        <div style={{
                                            flex: '0 0 40%', borderRight: `1px solid ${IDE_BORDER}`,
                                            position: 'relative', overflow: 'auto', backgroundColor: '#f5f5f5',
                                            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                                            padding: '8px',
                                        }}>
                                            <div style={{
                                                position: 'absolute', top: 6, left: 8, zIndex: 2,
                                                fontSize: '10px', color: '#795e26',
                                                background: 'rgba(255,255,255,0.9)', padding: '1px 8px', borderRadius: '3px',
                                                fontWeight: 600, letterSpacing: '0.04em',
                                            }}>
                                                ORIGINAL (Oracle Form)
                                            </div>
                                            <img
                                                src={`data:image/${screenshotFormat || 'png'};base64,${formScreenshot}`}
                                                alt={`Original Oracle Form: ${formName}`}
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', marginTop: '20px' }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    )}
                                    {/* Angular Preview iframe */}
                                    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                                        <iframe
                                            srcDoc={previewSrcDoc}
                                            sandbox="allow-same-origin"
                                            title="Angular Component Preview"
                                            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                        />
                                        <div style={{
                                            position: 'absolute', bottom: 8, right: 8,
                                            padding: '2px 8px', borderRadius: '3px', fontSize: '10px',
                                            backgroundColor: 'rgba(0,0,0,0.5)', color: '#ffffff',
                                            pointerEvents: 'none',
                                        }}>
                                            Visual layout preview
                                        </div>
                                    </div>
                                </div>
                                <div style={{ flex: '0 0 45%', overflow: 'auto', backgroundColor: '#fafafa' }}>
                                    <CodePanelContent
                                        tab={activePreviewTab}
                                        previewFiles={previewFiles}
                                        proposedChanges={[]}
                                        showDiff={false}
                                    />
                                </div>
                            </div>

                            {/* Bottom feedback bar */}
                            <FeedbackBar
                                view={view}
                                feedback={feedback}
                                setFeedback={setFeedback}
                                feedbackType={feedbackType}
                                setFeedbackType={setFeedbackType}
                                feedbackTypes={feedbackTypes}
                                loading={loading}
                                proposedChanges={proposedChanges}
                                selectedChanges={selectedChanges}
                                commitMessage={commitMessage}
                                setCommitMessage={setCommitMessage}
                                onEnhance={handleEnhance}
                                onConfirmAndPush={handleConfirmAndPush}
                                onRefine={handleRefine}
                            />
                        </div>
                    )}

                    {/* ── PROCESSING VIEW ── */}
                    {view === 'processing' && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: IDE_BG, gap: '16px' }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: '50%',
                                border: `3px solid ${IDE_BORDER}`, borderTopColor: IDE_ACCENT,
                                animation: 'spin 0.8s linear infinite',
                            }} />
                            <div style={{ fontSize: '14px', color: IDE_TEXT }}>Analyzing feedback and generating enhanced code...</div>
                            <div style={{ fontSize: '12px', color: IDE_TEXT_DIM }}>This may take up to 60 seconds</div>
                        </div>
                    )}

                    {/* ── ENHANCED PREVIEW VIEW (side-by-side Before / After) ── */}
                    {view === 'enhancedPreview' && (
                        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>

                            {/* Top bar: HTML/CSS/TS tabs */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '6px 12px', backgroundColor: IDE_SIDEBAR,
                                borderBottom: `1px solid ${IDE_BORDER}`, minHeight: '36px', flexShrink: 0,
                            }}>
                                <span style={{ fontSize: '11px', color: IDE_TEXT_DIM, marginRight: '2px' }}>Code diff:</span>
                                {['html', 'css', 'ts'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActivePreviewTab(tab)}
                                        style={{
                                            padding: '3px 10px', fontSize: '11px', borderRadius: '3px', border: 'none',
                                            cursor: 'pointer', textTransform: 'uppercase',
                                            backgroundColor: activePreviewTab === tab ? IDE_ACCENT : 'transparent',
                                            color: activePreviewTab === tab ? 'white' : IDE_TEXT_DIM,
                                            fontWeight: activePreviewTab === tab ? 600 : 400,
                                        }}
                                    >
                                        {tab}
                                    </button>
                                ))}
                                <span style={{ fontSize: '11px', color: IDE_TEXT_DIM, marginLeft: '12px' }}>
                                    {proposedChanges.length} file{proposedChanges.length !== 1 ? 's' : ''} changed
                                </span>
                                {formScreenshot && (
                                    <button
                                        onClick={() => setShowOracleModal(true)}
                                        style={{
                                            marginLeft: '12px', padding: '3px 10px', fontSize: '11px',
                                            borderRadius: '3px', border: `1px solid ${IDE_BORDER}`,
                                            backgroundColor: 'transparent', color: '#795e26',
                                            cursor: 'pointer', fontWeight: 600,
                                        }}
                                    >
                                        Oracle Form
                                    </button>
                                )}
                            </div>

                            {/* Side-by-side iframes (55%) + Code diff panel (45%) */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                {/* Two iframes side by side */}
                                <div style={{ flex: '0 0 55%', display: 'flex', borderBottom: `1px solid ${IDE_BORDER}` }}>
                                    {/* BEFORE */}
                                    <div style={{ flex: 1, borderRight: `1px solid ${IDE_BORDER}`, position: 'relative', overflow: 'hidden' }}>
                                        <div style={{
                                            position: 'absolute', top: 6, left: 8, zIndex: 2,
                                            fontSize: '10px', color: IDE_TEXT_DIM,
                                            background: 'rgba(255,255,255,0.85)', padding: '1px 8px', borderRadius: '3px',
                                            fontWeight: 600, letterSpacing: '0.04em',
                                        }}>
                                            BEFORE
                                        </div>
                                        <iframe
                                            srcDoc={previewSrcDoc}
                                            sandbox="allow-same-origin"
                                            title="Before"
                                            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                        />
                                    </div>
                                    {/* AFTER */}
                                    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                                        <div style={{
                                            position: 'absolute', top: 6, left: 8, zIndex: 2,
                                            fontSize: '10px', color: IDE_ACCENT,
                                            background: 'rgba(255,255,255,0.85)', padding: '1px 8px', borderRadius: '3px',
                                            fontWeight: 700, letterSpacing: '0.04em',
                                        }}>
                                            AFTER (AI)
                                        </div>
                                        <iframe
                                            srcDoc={enhancedSrcDoc}
                                            sandbox="allow-same-origin"
                                            title="After"
                                            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                        />
                                    </div>
                                </div>

                                {/* Code diff panel */}
                                <div style={{ flex: '0 0 45%', overflow: 'auto', backgroundColor: '#fafafa' }}>
                                    <CodePanelContent
                                        tab={activePreviewTab}
                                        previewFiles={previewFiles}
                                        proposedChanges={proposedChanges}
                                        showDiff={true}
                                    />
                                </div>
                            </div>

                            {/* Bottom confirm bar */}
                            <FeedbackBar
                                view={view}
                                feedback={feedback}
                                setFeedback={setFeedback}
                                feedbackType={feedbackType}
                                setFeedbackType={setFeedbackType}
                                feedbackTypes={feedbackTypes}
                                loading={loading}
                                proposedChanges={proposedChanges}
                                selectedChanges={selectedChanges}
                                commitMessage={commitMessage}
                                setCommitMessage={setCommitMessage}
                                onEnhance={handleEnhance}
                                onConfirmAndPush={handleConfirmAndPush}
                                onRefine={handleRefine}
                            />
                        </div>
                    )}

                    {/* ── FILE PREVIEW VIEW (legacy) ── */}
                    {view === 'filePreview' && (
                        <div className="flex-1 overflow-y-auto" style={{ padding: '32px 48px', backgroundColor: IDE_BG }}>
                            <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                                <div style={{ marginBottom: '24px' }}>
                                    <h2 style={{ fontSize: '18px', fontWeight: 600, color: IDE_TEXT, marginBottom: '6px' }}>Analysis Complete</h2>
                                    {analysis && (
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
                                            {[
                                                { label: 'Type', value: analysis.feedback_type?.replace('_', ' ') },
                                                { label: 'Scope', value: analysis.scope },
                                                { label: 'Area', value: analysis.affected_area },
                                                { label: 'Confidence', value: `${Math.round((analysis.confidence || 0) * 100)}%` },
                                            ].map(item => item.value && (
                                                <div key={item.label} style={{ padding: '4px 10px', backgroundColor: '#f5f5f5', borderRadius: '3px', border: `1px solid ${IDE_BORDER}`, fontSize: '12px' }}>
                                                    <span style={{ color: IDE_TEXT_DIM }}>{item.label}: </span>
                                                    <span style={{ color: IDE_GREEN, fontWeight: 600 }}>{item.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 700, color: IDE_TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                                        Files to be Modified ({affectedFiles.length})
                                    </div>
                                    {affectedFiles.length === 0 ? (
                                        <div style={{ color: IDE_TEXT_DIM, fontSize: '13px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                            No files identified for modification
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {affectedFiles.map((filePath, idx) => {
                                                const fileName = filePath.split('/').pop();
                                                const isBackend = filePath.includes('/backend/');
                                                return (
                                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', border: `1px solid ${IDE_BORDER}` }}>
                                                        <IoDocument style={{ width: 14, height: 14, color: getFileIcon(fileName), flexShrink: 0 }} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: '13px', color: IDE_TEXT, fontWeight: 500 }}>{fileName}</div>
                                                            <div style={{ fontSize: '11px', color: IDE_TEXT_DIM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={filePath}>{filePath}</div>
                                                        </div>
                                                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: isBackend ? 'rgba(139,92,246,0.2)' : 'rgba(59,130,246,0.2)', color: isBackend ? '#a78bfa' : '#60a5fa', flexShrink: 0 }}>
                                                            {isBackend ? 'Backend' : 'Frontend'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {contextInfo && (
                                    <div style={{ marginBottom: '24px', padding: '12px 16px', backgroundColor: '#f5f5f5', borderRadius: '4px', border: `1px solid ${IDE_BORDER}`, display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                        {[
                                            { label: 'Business Rules', value: contextInfo.business_rules_count || 0 },
                                            { label: 'Validation Rules', value: contextInfo.validation_rules_count || 0 },
                                            { label: 'Similar Feedback', value: contextInfo.similar_feedback?.length || 0 },
                                        ].map(item => (
                                            <div key={item.label} style={{ fontSize: '13px' }}>
                                                <span style={{ color: IDE_TEXT_DIM }}>{item.label}: </span>
                                                <span style={{ color: IDE_TEXT, fontWeight: 600 }}>{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => setView('input')}
                                        style={{ padding: '8px 18px', fontSize: '13px', backgroundColor: 'transparent', border: `1px solid ${IDE_BORDER}`, color: IDE_TEXT, borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        ← Edit Feedback
                                    </button>
                                    <button
                                        onClick={handleProcessFeedback}
                                        disabled={loading || affectedFiles.length === 0}
                                        style={{
                                            padding: '8px 22px', fontSize: '13px', fontWeight: 600,
                                            backgroundColor: (loading || affectedFiles.length === 0) ? '#e0e0e0' : IDE_ACCENT,
                                            color: (loading || affectedFiles.length === 0) ? IDE_TEXT_DIM : 'white',
                                            border: 'none', borderRadius: '4px', cursor: (loading || affectedFiles.length === 0) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loading ? 'Generating Changes...' : 'Generate Changes →'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── CHANGES VIEW (legacy) ── */}
                    {view === 'changes' && (
                        <div className="flex flex-1 flex-col min-h-0">
                            {proposedChanges.length === 0 ? (
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: IDE_TEXT_DIM, fontSize: '14px' }}>
                                    No changes were generated. Try refining your feedback.
                                </div>
                            ) : activeChange ? (
                                <>
                                    <div style={{ padding: '6px 16px', fontSize: '12px', color: IDE_TEXT_DIM, backgroundColor: IDE_BG, borderBottom: `1px solid ${IDE_BORDER}`, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {activeChange.file_path.split('/').map((part, i, arr) => (
                                            <React.Fragment key={i}>
                                                <span style={{ color: i === arr.length - 1 ? IDE_TEXT : IDE_TEXT_DIM }}>{part}</span>
                                                {i < arr.length - 1 && <span style={{ color: IDE_BORDER }}>/</span>}
                                            </React.Fragment>
                                        ))}
                                        {activeChange.description && (
                                            <span style={{ marginLeft: '12px', padding: '1px 8px', backgroundColor: '#f5f5f5', borderRadius: '10px', color: IDE_YELLOW, fontSize: '11px' }}>
                                                {activeChange.description}
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex-1 overflow-auto" style={{ backgroundColor: '#fafafa' }}>
                                        {viewMode === 'code' ? (
                                            <pre style={{
                                                margin: 0, padding: '16px 0', fontSize: '13px', lineHeight: '20px',
                                                fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
                                                color: '#1f1f1f', whiteSpace: 'pre', overflowX: 'auto',
                                                minHeight: '100%', backgroundColor: '#fafafa'
                                            }}>
                                                {(activeChange.full_content || activeChange.preview || '').split('\n').map((line, i) => (
                                                    <div key={i} style={{ display: 'flex', minWidth: 'max-content' }}>
                                                        <span style={{
                                                            display: 'inline-block', width: '52px', minWidth: '52px', textAlign: 'right',
                                                            paddingRight: '16px', color: '#aaaaaa', userSelect: 'none', flexShrink: 0
                                                        }}>
                                                            {i + 1}
                                                        </span>
                                                        <span style={{ paddingRight: '32px' }}>{line}</span>
                                                    </div>
                                                ))}
                                            </pre>
                                        ) : (
                                            <ReactDiffViewer
                                                oldValue=""
                                                newValue={activeChange.full_content || activeChange.preview || ''}
                                                splitView={false}
                                                compareMethod={DiffMethod.WORDS}
                                                showDiffOnly={false}
                                                useDarkTheme={true}
                                                leftTitle="Original"
                                                rightTitle="Modified"
                                                styles={{
                                                    variables: {
                                                        dark: {
                                                            diffViewerBackground: '#ffffff',
                                                            diffViewerColor: '#1f1f1f',
                                                            addedBackground: 'rgba(46,160,67,0.2)',
                                                            addedColor: '#4ec9b0',
                                                            removedBackground: 'rgba(248,81,73,0.2)',
                                                            removedColor: '#f14c4c',
                                                            gutterBackground: '#f3f3f3',
                                                            gutterColor: '#6e6e6e',
                                                        },
                                                    },
                                                    line: { padding: '1px 0', fontFamily: '"Cascadia Code","Consolas",monospace', fontSize: '13px' },
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div style={{
                                        flexShrink: 0,
                                        display: 'flex', alignItems: 'center', gap: '12px',
                                        padding: '10px 16px',
                                        backgroundColor: '#ffffff',
                                        borderTop: `1px solid ${IDE_BORDER}`,
                                    }}>
                                        <input
                                            type="text"
                                            value={commitMessage}
                                            onChange={(e) => setCommitMessage(e.target.value)}
                                            placeholder="Commit message (optional)..."
                                            style={{
                                                flex: 1, padding: '7px 12px', fontSize: '13px',
                                                backgroundColor: '#f5f5f5', border: `1px solid ${IDE_BORDER}`,
                                                color: IDE_TEXT, borderRadius: '4px', outline: 'none'
                                            }}
                                        />
                                        <span style={{ fontSize: '12px', color: IDE_TEXT_DIM, whiteSpace: 'nowrap' }}>
                                            {Object.values(selectedChanges).filter(Boolean).length} of {proposedChanges.length} selected
                                        </span>
                                        <button
                                            onClick={handleCommitChanges}
                                            disabled={loading || Object.values(selectedChanges).filter(Boolean).length === 0}
                                            style={{
                                                padding: '7px 20px', fontSize: '13px', fontWeight: 600,
                                                backgroundColor: Object.values(selectedChanges).filter(Boolean).length === 0 ? '#e0e0e0' : IDE_ACCENT,
                                                color: Object.values(selectedChanges).filter(Boolean).length === 0 ? IDE_TEXT_DIM : 'white',
                                                border: 'none', borderRadius: '4px',
                                                cursor: (loading || Object.values(selectedChanges).filter(Boolean).length === 0) ? 'not-allowed' : 'pointer',
                                                whiteSpace: 'nowrap', flexShrink: 0
                                            }}
                                        >
                                            {loading ? 'Committing...' : 'Commit Selected Changes'}
                                        </button>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    )}

                    {/* ── COMMIT COMPLETE VIEW ── */}
                    {view === 'commit' && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: IDE_BG, gap: '12px' }}>
                            <IoCheckmarkCircle style={{ width: 64, height: 64, color: '#4ade80' }} />
                            <h3 style={{ fontSize: '22px', fontWeight: 600, color: IDE_TEXT, margin: 0 }}>Changes Pushed to ADO</h3>
                            <p style={{ fontSize: '14px', color: IDE_TEXT_DIM, margin: 0 }}>Your feedback has been applied and the updated code has been pushed to Azure DevOps.</p>
                            <button
                                onClick={handleClose}
                                style={{ marginTop: '16px', padding: '10px 28px', fontSize: '14px', fontWeight: 600, backgroundColor: IDE_ACCENT, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Oracle Form Screenshot Modal */}
            {showOracleModal && formScreenshot && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                    }}
                    onClick={() => setShowOracleModal(false)}
                >
                    <div
                        style={{
                            position: 'relative', maxWidth: '90vw', maxHeight: '90vh',
                            backgroundColor: '#ffffff', borderRadius: '8px',
                            padding: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: '12px',
                        }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#795e26' }}>
                                Original Oracle Form: {formName}
                            </span>
                            <button
                                onClick={() => setShowOracleModal(false)}
                                style={{
                                    background: 'none', border: 'none', fontSize: '20px',
                                    color: IDE_TEXT_DIM, cursor: 'pointer', padding: '0 4px',
                                }}
                            >
                                x
                            </button>
                        </div>
                        <img
                            src={`data:image/${screenshotFormat || 'png'};base64,${formScreenshot}`}
                            alt={`Original Oracle Form: ${formName}`}
                            style={{
                                maxWidth: '85vw', maxHeight: '80vh',
                                objectFit: 'contain', borderRadius: '4px',
                                border: `1px solid ${IDE_BORDER}`,
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Loading overlay (only for non-processing views) */}
            {loading && view !== 'processing' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
                    <div style={{ padding: '24px 32px', backgroundColor: '#f5f5f5', borderRadius: '6px', border: `1px solid ${IDE_BORDER}`, textAlign: 'center' }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: '50%', border: `3px solid ${IDE_BORDER}`,
                            borderTopColor: IDE_ACCENT, animation: 'spin 0.8s linear infinite', margin: '0 auto 12px'
                        }} />
                        <div style={{ fontSize: '13px', color: IDE_TEXT }}>Processing...</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormFeedback;
